import { htmlToMarkdown, htmlToPlainText } from '../core/html-conversion';
import { sanitizeHtml } from '../core/sanitize';

export type DownloadFormat = 'txt' | 'html' | 'md' | 'docx' | 'pdf';

export function createDownloadFilename(extension: DownloadFormat, date = new Date()): string {
  const isoDate = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
  return `bereinigter-text-${isoDate}.${extension}`;
}

function fullHtmlDocument(body: string): string {
  return `<!doctype html>\n<html lang="de">\n<head>\n<meta charset="utf-8">\n<meta name="viewport" content="width=device-width,initial-scale=1">\n<title>Bereinigter Text</title>\n</head>\n<body>\n${sanitizeHtml(body)}\n</body>\n</html>`;
}

export async function createDownload(format: DownloadFormat, canonicalHtml: string): Promise<{ blob: Blob; filename: string }> {
  let blob: Blob;
  if (format === 'docx') {
    const { createDocxBlob } = await import('./docx-export');
    blob = await createDocxBlob(canonicalHtml);
  } else if (format === 'pdf') {
    const { createPdfBlob } = await import('./pdf-export');
    blob = await createPdfBlob(canonicalHtml);
  } else if (format === 'html') {
    blob = new Blob([fullHtmlDocument(canonicalHtml)], { type: 'text/html;charset=utf-8' });
  } else if (format === 'md') {
    blob = new Blob([htmlToMarkdown(canonicalHtml)], { type: 'text/markdown;charset=utf-8' });
  } else {
    blob = new Blob([htmlToPlainText(canonicalHtml)], { type: 'text/plain;charset=utf-8' });
  }
  return { blob, filename: createDownloadFilename(format) };
}

export async function triggerDownload(format: DownloadFormat, canonicalHtml: string): Promise<string> {
  const { blob, filename } = await createDownload(format, canonicalHtml);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
  return filename;
}
